import { I18n } from '@ies/starling_intl';
import _axios, { AxiosRequestConfig } from 'axios';
import { message } from 'antd';

import { baseURL } from '@/common/const';
import { defaultLang } from '@/common/starling';
import { Modal } from '@arco-design/web-react';

/** 后端返回的数据格式 */
interface ResponseData {
  right_key: any | undefined;
  project_id: any | undefined;
  data: any;
  status: {
    code: number;
    message: string;
  };
}

type ExtendedAxiosRequestConfig = AxiosRequestConfig & {
  noErrorMessage?: boolean;
};

/** ReactRouter 的 history 实例，在 App.tsx 中初始化 */
let browserHistory;
export const setBrowserHistory = history => {
  browserHistory = history;
};

/**
 * GameFlow 定制版 axios
 *
 * @example
 *
 * ```
 * axios.get('/v1/users/self')
 *   .then(({data}) => {
 *     console.log(data.user);
 *   });
 *   .catch(({code, message}) => {
 *     console.log(message);
 *   });
 * ```
 */
const axios = _axios.create({
  // timeout: 80000,
  baseURL,
  withCredentials: true,
  headers: {
    'gf-lang': defaultLang,
  },
});

// 默认全局报错，传 `noErrorMessage = true` 就不全局报错
axios.interceptors.response.use(
  function onSuccess(res) {
    const { data } = res as { data: ResponseData };
    if (data?.status?.code === 0) {
      return data.data;
    }
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject(
      data?.status
        ? {
            _isAxiosReject: true,
            ...data.status,
          }
        : {
            _isAxiosReject: true,
            code: res.status,
            message: res.statusText,
          },
    );
  },
  function onError(err: {
    config: any;
    response: {
      data: ResponseData;
      code: number;
      statusText: string;
      status: number;
    };
  }) {
    // 网络连接错误
    if (!err.response) {
      // @ts-expect-error
      err.response = { code: err.code, statusText: err.message };
    }

    const {
      response: { data },
    } = err;

    // 用户未登录
    if (data?.status?.code === 2003) {
      if (
        !['/login', '/signout', '/', '/download'].includes(
          window.location.pathname,
        ) &&
        !window.location.pathname.includes('/share') &&
        !window.location.pathname.includes('/summary') &&
        !window.location.pathname.includes('/noPermission/download')
      ) {
        localStorage.setItem(
          'redirect_path',
          window.location.pathname + window.location.search,
        );
        browserHistory?.push('/signout');
      }
      err.config.noErrorMessage = true; // 报错在 Vue 侧报，React 侧不重复报错
    }

    // 418 无权限报错
    if (err.response.status === 418) {
      // const key = data.right_key;
      const roles = ['role_member', 'role_temporary_member'];
      const projectID = data.project_id;
      // eslint-disable-next-line promise/no-promise-in-callback
      // axios
      //   .get(`/v1/access_control/right/role?right_key=${key}`)
      //   .then(data => {
      //     const roles = (data as any)?.roles;
      //     if (roles.length) {
      //       if (!window.location.pathname.includes('/role-permissions-apply')) {
      //         localStorage.removeItem('redirect_path');
      //         window.location.replace(
      //           `${
      //             window.location.origin
      //           }/role-permissions-apply/${projectID}/${JSON.stringify(roles)}`,
      //         );
      //       }
      //     }
      //   })
      //   .catch(_ => {});
      if (!window.location.pathname.includes('/role-permissions-apply')) {
        localStorage.removeItem('redirect_path');
        window.location.replace(
          `${
            window.location.origin
          }/role-permissions-apply/${projectID}/${JSON.stringify(roles)}`,
        );
      }
    }

    // 2222 测试包管理-同步字节内测无权限报错
    if (err.response.status === 403) {
      if (err.response.data.status.code === 2222) {
        err.config.noErrorMessage = true;
        Modal.error({
          title: I18n.t('tips', {}, '提示'),
          content: err.response.data.status.message,
        });
      }
    }

    // 如果没有提供 `noErrorMessage` 标记默认进行全局错误提示
    if (err.message === 'cancleRequest') {
      message.destroy();
    } else if (!err.config?.noErrorMessage) {
      message.destroy(); // 关闭其他全局提示，如有
      message.error(data?.status?.message || err.response.statusText);
    }
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject(
      data?.status
        ? {
            _isAxiosReject: true,
            ...data.status,
            status: err.response.status,
          }
        : {
            _isAxiosReject: true,
            code: err.response.code,
            message: err.response.statusText,
            status: err.response.status,
          },
    );
  },
);

// 屏蔽报错，允许业务代码省略对接口报错的处理
window.addEventListener('unhandledrejection', e => {
  if (e.reason?._isAxiosReject) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }
});

export const api = {
  get: <T = any>(
    url: string,
    config?: ExtendedAxiosRequestConfig,
  ): Promise<T> => axios.get(url, config) as any,
  post: <T = any>(
    url: string,
    data?: any,
    config?: ExtendedAxiosRequestConfig,
  ): Promise<T> => axios.post(url, data, config) as any,
  put: <T = any>(
    url: string,
    data?: any,
    config?: ExtendedAxiosRequestConfig,
  ): Promise<T> => axios.put(url, data, config) as any,
  delete: <T = any>(
    url: string,
    config?: ExtendedAxiosRequestConfig,
  ): Promise<T> => axios.delete(url, config) as any,
  patch: <T = any>(
    url: string,
    data?: any,
    config?: ExtendedAxiosRequestConfig,
  ): Promise<T> => axios.patch(url, data, config) as any,
};

export default axios;
